<template>
  <div class="tr-professional" :class="{collapsed: !open}">
    <div class="tr-professional-head">
      <div class="tr-identity">
        <div class="tr-avatar">{{ avatar }}</div>
        <div>
          <div class="tr-name">
            {{ professional.name }}
            <span class="tr-warn tooltip tooltip-right" v-if="totals.hasInconsistencies"
                  data-tooltip="Há atendimento sem regra de repasse">
              <fa-icon :icon="['fal', 'info-circle']"/>
            </span>
          </div>
          <div class="tr-council" v-if="council">{{ council }}</div>
        </div>
      </div>
      <div class="tr-totals">
        <div class="tr-total" :class="{zero: !totals.lot}" v-if="showOrigin('lot')">
          <small>Lote</small>
          <div class="tr-total-value">{{ totals.lot | currency }}</div>
        </div>
        <div class="tr-total" :class="{zero: !totals.cashRegister}"
             v-if="showOrigin('cashRegister')">
          <small>Particular</small>
          <div class="tr-total-value">{{ totals.cashRegister | currency }}</div>
        </div>
        <div class="tr-total" :class="{zero: !totals.medicalReport}"
             v-if="showOrigin('medicalReport')">
          <small>Laudos</small>
          <div class="tr-total-value">{{ totals.medicalReport | currency }}</div>
        </div>
        <div class="tr-total" :class="{zero: !totals.adjustments}">
          <small>Ajustes</small>
          <div class="tr-total-value" :class="adjustmentsClass">
            {{ signedCurrency(totals.adjustments) }}
          </div>
        </div>
        <div class="tr-total main">
          <small>A pagar</small>
          <div class="tr-total-value">{{ totals.payable | currency }}</div>
        </div>
      </div>
      <div class="tr-actions">
        <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                :data-tooltip="consolidateTooltip"
                :disabled="!canConsolidate || pendingRows.length === 0"
                @click="$emit('consolidate', pendingRows)">
          <fa-icon :icon="['fal', 'file-invoice-dollar']"/>
        </button>
        <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                data-tooltip="Recibo do repasse" :disabled="!canPrint"
                @click="$emit('receipt', group)">
          <fa-icon :icon="['fal', 'print']"/>
        </button>
        <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                :data-tooltip="open ? 'Recolher' : 'Expandir'" @click="open = !open">
          <fa-icon class="tr-chevron" :icon="['fal', 'chevron-down']"/>
        </button>
      </div>
    </div>
    <div class="tr-body" v-if="open">
      <adjustments-block :items="group.adjustments" v-if="group.adjustments.length > 0"
                         @open-batch="$emit('open-batch', $event)"/>

      <origin-panel type="lot" v-if="showOrigin('lot')"
                    :count="origins.lot.count" :payable="lotPayable" :paid="lotPaid">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 28px"></th>
              <th>Lote</th>
              <th class="hide-sm">Convênio</th>
              <th class="hide-sm">Vencimento</th>
              <th class="text-right">Bruto</th>
              <th class="text-right">Ajustes</th>
              <th class="text-right">A pagar</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <batch-row v-for="item in origins.lot.items" :key="item.id"
                     :item="item" :can-pay="canPay" :can-print="canPrint"
                     @pay="$emit('pay', $event.financialId)"
                     @print="$emit('print', $event)"
                     @open-batch="$emit('open-batch', $event.batch.id)"/>
        </table>
      </origin-panel>

      <origin-panel type="consolidation" v-if="showConsolidations"
                    :count="group.consolidations.length"
                    :payable="consolidationPayable" :paid="consolidationPaid"
                    custom-title="Consolidados" custom-subtitle="Contas a pagar criadas nesta tela">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 28px"></th>
              <th>Documento</th>
              <th class="hide-sm">Vencimento</th>
              <th class="text-right">Ajustes</th>
              <th class="text-right">A pagar</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <consolidation-row v-for="item in group.consolidations" :key="item.id"
                             :item="item" :can-pay="canPay" :can-print="canPrint"
                             @pay="$emit('pay', $event.payableId || $event.id)"
                             @print="$emit('print', $event)"/>
        </table>
      </origin-panel>

      <origin-panel type="cashRegister" v-if="showOrigin('cashRegister')"
                    :count="origins.cashRegister.count" :pending="cashPending">
        <table class="table">
          <thead>
            <tr>
              <th class="tr-check">
                <label class="form-checkbox">
                  <input type="checkbox" :checked="allSelected('cashRegister')"
                         :indeterminate.prop="partiallySelected('cashRegister')"
                         :disabled="selectable('cashRegister').length === 0"
                         @change="toggleAll('cashRegister', $event.target.checked)">
                  <i class="form-icon"></i>
                </label>
              </th>
              <th>Data</th>
              <th>Paciente</th>
              <th>Serviço</th>
              <th class="text-right">Recebido</th>
              <th class="text-right hide-sm">Regra</th>
              <th class="text-right">Repasse</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <cash-row v-for="item in origins.cashRegister.items" :key="item.id"
                    :item="item" :checked="isSelected(item)" :can-pay="canPay" :can-print="canPrint"
                    :document="documentOf(item)"
                    :printing-entry="printingEntryId === String(item.entryId)"
                    @check="$emit('select', [item], $event)"
                    @pay="$emit('pay', $event.payableId || $event.financialId)"
                    @print="$emit('print', $event)"
                    @open-entry="$emit('open-entry', $event)"/>
        </table>
      </origin-panel>

      <origin-panel type="medicalReport" v-if="showOrigin('medicalReport')"
                    :count="origins.medicalReport.count" :pending="reportPending">
        <table class="table">
          <thead>
            <tr>
              <th class="tr-check">
                <label class="form-checkbox">
                  <input type="checkbox" :checked="allSelected('medicalReport')"
                         :indeterminate.prop="partiallySelected('medicalReport')"
                         :disabled="selectable('medicalReport').length === 0"
                         @change="toggleAll('medicalReport', $event.target.checked)">
                  <i class="form-icon"></i>
                </label>
              </th>
              <th>Assinado em</th>
              <th>Paciente</th>
              <th>Exame</th>
              <th class="hide-sm">Convênio</th>
              <th class="text-right">Valor do laudo</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <report-row v-for="item in origins.medicalReport.items" :key="item.id"
                      :item="item" :checked="isSelected(item)"
                      :can-pay="canPay" :can-print="canPrint" :document="documentOf(item)"
                      @check="$emit('select', [item], $event)"
                      @pay="$emit('pay', $event.payableId || $event.financialId)"
                      @print="$emit('print', $event)"
                      @open-report="$emit('open-report', $event)"/>
        </table>
      </origin-panel>
    </div>
  </div>
</template>

<script>
import AdjustmentsBlock from './AdjustmentsBlock.vue';
import OriginPanel from './OriginPanel.vue';
import BatchRow from './BatchRow.vue';
import ConsolidationRow from './ConsolidationRow.vue';
import CashRow from './CashRow.vue';
import ReportRow from './ReportRow.vue';
import {
  initials, councilLabel, signedCurrency, selectionKey, isSelectable,
} from '../helpers';

export default {
  components: {
    AdjustmentsBlock,
    OriginPanel,
    BatchRow,
    ConsolidationRow,
    CashRow,
    ReportRow,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    selectedKeys: {
      type: Array,
      default: () => [],
    },
    originFilter: {
      type: String,
      default: 'all',
    },
    canPay: {
      type: Boolean,
      default: false,
    },
    canPrint: {
      type: Boolean,
      default: false,
    },
    canConsolidate: {
      type: Boolean,
      default: false,
    },
    defaultOpen: {
      type: Boolean,
      default: true,
    },
    printingEntryId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      open: this.defaultOpen,
    };
  },
  computed: {
    professional() {
      return this.group.professional || {};
    },
    origins() {
      return this.group.origins;
    },
    totals() {
      return this.group.totals || {};
    },
    avatar() {
      return initials(this.professional.name);
    },
    council() {
      return councilLabel(this.professional);
    },
    adjustmentsClass() {
      const value = Number(this.totals.adjustments) || 0;
      if (value < 0) return 'text-error';
      if (value > 0) return 'text-green';
      return '';
    },
    pendingRows() {
      return [...this.origins.cashRegister.items, ...this.origins.medicalReport.items]
        .filter(isSelectable);
    },
    consolidateTooltip() {
      if (!this.canConsolidate) return 'Sem permissão para consolidar';
      if (this.pendingRows.length === 0) return 'Nada pendente para consolidar';
      return 'Consolidar pendentes';
    },
    showConsolidations() {
      return this.group.consolidations.length > 0 && this.originFilter !== 'lot';
    },
    lotPayable() {
      return this.sumWhere(this.origins.lot.items, row => row.status === 'consolidated', 'total');
    },
    lotPaid() {
      return this.sumWhere(this.origins.lot.items, row => row.status === 'paid', 'total');
    },
    consolidationPayable() {
      return this.sumWhere(
        this.group.consolidations, row => row.transferStatus !== 'paid', 'openTotal',
      );
    },
    consolidationPaid() {
      return this.sumWhere(
        this.group.consolidations, row => row.paidTotal > 0, 'paidTotal',
      );
    },
    cashPending() {
      return this.sumWhere(
        this.origins.cashRegister.items, row => row.status === 'pending', 'transfer',
      );
    },
    reportPending() {
      return this.sumWhere(
        this.origins.medicalReport.items, row => row.status === 'pending', 'transfer',
      );
    },
    documents() {
      const map = {};
      this.group.consolidations.forEach((row) => {
        map[String(row.bundleId)] = row.document;
      });
      return map;
    },
  },
  methods: {
    signedCurrency,
    sumWhere(list, predicate, field) {
      return list.filter(predicate).reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
    },
    showOrigin(origin) {
      return this.originFilter === 'all' || this.originFilter === origin;
    },
    isSelected(row) {
      return this.selectedKeys.includes(selectionKey(row));
    },
    selectable(origin) {
      return this.origins[origin].items.filter(isSelectable);
    },
    allSelected(origin) {
      const rows = this.selectable(origin);
      return rows.length > 0 && rows.every(row => this.isSelected(row));
    },
    partiallySelected(origin) {
      const rows = this.selectable(origin);
      const count = rows.filter(row => this.isSelected(row)).length;
      return count > 0 && count < rows.length;
    },
    toggleAll(origin, checked) {
      this.$emit('select', this.selectable(origin), checked);
    },
    documentOf(row) {
      return row.bundleId ? this.documents[String(row.bundleId)] || null : null;
    },
  },
};
</script>
